<template>
  <div class="login-centered">
    <div class="login-centered-inner">
      <div class="login-brand-block">
        <img
          src="/media/logos/hilight-logo-icon.png"
          alt="Hilight"
          class="login-brand-logo"
        />
        <div class="login-brand-text">
          <div class="login-brand-name">HILIGHT</div>
          <div class="login-brand-tagline">HILIGHT TECH</div>
        </div>
      </div>

      <span class="login-version-badge">
        <i class="fa-solid fa-shield-halved"></i>
        ERP nội bộ · v{{ appVersionShort }}
      </span>

      <div class="login-form-header">
        <h1 class="login-form-title">{{ step[mode].headerTitle }}</h1>
        <p class="login-form-subtitle">{{ step[mode].subtitle }}</p>
      </div>

      <div class="login-form-container">
        <p
          v-if="mode !== 'welcome'"
          class="login-form-card-title font-weight-bold mb-5"
        >
          {{ step[mode].title }}
        </p>

        <b-form class="form" @submit.stop.prevent="onLogin">
          <div
            v-if="errors"
            role="alert"
            v-bind:class="{ show: errors && errors.length }"
            class="alert fade alert-danger"
          >
            <div class="alert-text">
              {{ errors }}
            </div>
          </div>

          <transition name="fade-in-up">
            <div>
              <LoginUsername
                v-if="mode === 'welcome'"
                :ref="step.welcome.ref"
                :username="username"
                @forgot-password="onForgotPassword"
              />
              <ResetPassword
                v-if="mode === 'password'"
                :ref="step.password.ref"
                :username="username"
                :token="token"
              />
              <ForgotPassword
                v-if="mode === 'forgot-password'"
                :ref="step['forgot-password'].ref"
                :username="username"
                v-on:change-mode="onEmitChangeMode"
                v-on:update-username="onEmitUpdateUsername"
              />
              <FailedPassword
                v-if="mode === 'failed-password'"
                :username="username"
                v-on:change-mode="onEmitChangeMode"
              />
            </div>
          </transition>

          <div
            v-if="['welcome', 'password'].includes(mode)"
            class="form-group mt-1 mb-0"
          >
            <button
              ref="login_submit"
              type="submit"
              class="submit-button w-100 btn font-weight-bold"
              :disabled="isSubmitting"
            >
              <span
                v-if="isSubmitting"
                class="login-btn-spinner"
                aria-hidden="true"
              ></span>
              {{ isSubmitting ? 'Đang đăng nhập...' : step[mode].titleBtn }}
              <i
                v-if="!isSubmitting"
                class="fa-solid fa-arrow-right ml-2"
              ></i>
            </button>
          </div>

          <p v-if="mode === 'welcome'" class="login-security-note mt-4 mb-0">
            <i class="fa-solid fa-shield-halved text-success mr-2"></i>
            Phiên đăng nhập được mã hóa & ghi log nội bộ.
          </p>
        </b-form>
      </div>

      <p v-if="mode === 'welcome'" class="login-help-text">
        Chưa có tài khoản? Liên hệ
        <strong>phòng IT</strong> để được cấp quyền.
      </p>
      <p v-else-if="mode === 'forgot-password'" class="login-help-text">
        <a href="javascript:void(0)" @click="onEmitChangeMode('welcome')">
          ← Quay lại đăng nhập
        </a>
      </p>

      <div class="login-form-bottom">
        <router-link to="/" class="login-back-link">
          ← Về trang chủ
        </router-link>
        <span class="login-bottom-dot">·</span>
        <span class="login-version-text">v{{ appVersion }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import {
  LOGIN,
  LOGOUT,
  RESET_PASSWORD,
  TOKEN_RESET_PASSWORD,
} from '@/core/services/store/modules/auth.module';
import { mapState } from 'vuex';
import { TIME_TRIGGER } from '@/utils/constants';
import { validationMixin } from 'vuelidate';
import LoginUsername from '@/view/components/auth/LoginUsername.vue';
import ResetPassword from '@/view/components/auth/ResetPassword.vue';
import ForgotPassword from '@/view/components/auth/ForgotPassword.vue';
import FailedPassword from '@/view/components/auth/FailedPassword.vue';

export default {
  mixins: [validationMixin],
  name: 'login',
  data() {
    return {
      firstTimeLogin: false,
      isSubmitting: false,
      mode: 'welcome',
      step: {
        welcome: {
          headerTitle: 'Chào mừng trở lại',
          subtitle: 'Đăng nhập bằng tài khoản nội bộ',
          title: 'Đăng nhập',
          titleBtn: 'Đăng nhập',
          ref: 'welcome-user-name',
          url: LOGIN,
        },
        password: {
          headerTitle: 'Thay đổi mật khẩu',
          subtitle: 'Tạo mật khẩu mới để bảo mật tài khoản của bạn.',
          title: 'Mật khẩu mới',
          titleBtn: 'Thay đổi mật khẩu',
          ref: 'change-password',
          url: RESET_PASSWORD,
        },
        otp: {
          headerTitle: 'Chào mừng trở lại',
          subtitle: 'Nhập mã xác thực được gửi đến thiết bị của bạn.',
          title: 'Nhập mã otp của bạn',
          ref: 'welcome-otp',
        },
        'forgot-password': {
          headerTitle: 'Quên mật khẩu?',
          subtitle: 'Nhập email hoặc số điện thoại để nhận hướng dẫn đặt lại.',
          title: 'Lấy lại mật khẩu',
          titleBtn: 'Gửi mail',
          ref: 'forgot-password',
        },
        'failed-password': {
          headerTitle: 'Có lỗi xảy ra',
          subtitle: 'Liên kết đặt lại mật khẩu không hợp lệ hoặc đã hết hạn.',
          title: 'Có lỗi xảy ra',
          titleBtn: 'Trở về',
          ref: 'failed-password',
        },
      },
      submitButton: null,
      username: null,
      token: null,
    };
  },
  components: {
    LoginUsername,
    ResetPassword,
    ForgotPassword,
    FailedPassword,
  },
  validations: {},
  created() {
    this.checkAuthUser();
    const type = this.$route.query.type || null;
    const username = this.$route.query.username || null;
    const token = this.$route.query.token || null;
    switch (type) {
      case 'change-password': {
        this.mode = 'password';
        this.username = username;
        this.checkToken(token);
        break;
      }
      default:
        break;
    }
  },
  methods: {
    isFromEmail() {
      return this.$route.query.token;
    },
    getElementSubmit() {
      return this.$refs['login_submit'];
    },
    onLoadingSubmit() {
      this.isSubmitting = true;
      this.submitButton = this.getElementSubmit();
      if (this.submitButton) {
        this.submitButton.classList.add(
          'spinner',
          'spinner-light',
          'spinner-right',
        );
      }
    },
    unLoadingSubmit() {
      this.isSubmitting = false;
      if (this.submitButton) {
        this.submitButton.classList.remove(
          'spinner',
          'spinner-light',
          'spinner-right',
        );
      }
    },
    onLogin() {
      let data = {};
      let error = true;
      switch (this.mode) {
        case 'welcome': {
          error = this.$refs[this.step.welcome.ref].catchAnyError();
          data = this.$refs[this.step.welcome.ref].welcome;
          this.$refs[this.step.welcome.ref].persistRememberMe();
          break;
        }
        case 'password': {
          data.token = null;
          error = this.$refs[this.step.password.ref].catchAnyError();
          const { changePassword } = this.$refs[this.step.password.ref];
          data.username = this.username;
          data.password = changePassword.newpassword;
          data.token = this.$route.query.token || null;
          break;
        }
        case 'otp': {
          this.$refs[this.step.otp.ref].catchAnyError();
          break;
        }
        default:
          break;
      }
      if (error) return;

      this.$store.dispatch(LOGOUT);
      this.onLoadingSubmit();

      this.$store
        .dispatch(this.step[this.mode].url, data)
        .then((result) => {
          switch (this.mode) {
            case 'welcome': {
              if (result.isNextPassword) {
                this.mode = 'password';
                this.username = result.username;
              } else {
                this.$router.push({ name: 'dashboard' });
                setTimeout(() => {
                  this.$router.go(this.$router.currentRoute);
                }, TIME_TRIGGER);
              }
              break;
            }
            case 'password': {
              if (result.username) {
                this.mode = 'welcome';
                this.$refs[this.step.welcome.ref].welcome.username =
                  result.username;
              }
              break;
            }
            case 'otp':
              break;
            default:
              break;
          }
          this.unLoadingSubmit();
        })
        .catch(() => {
          this.unLoadingSubmit();
        });
    },
    checkAuthUser() {
      if (this.isAuthenticated && !this.isFromEmail()) {
        this.$router.push({ name: 'dashboard' });
      }
    },
    onEmitChangeMode(mode) {
      this.mode = mode;
    },
    onForgotPassword() {
      this.onEmitChangeMode('forgot-password');
    },
    onEmitUpdateUsername(payload) {
      this.username = payload ? payload.username : null;
    },
    checkToken(token) {
      this.$store
        .dispatch(TOKEN_RESET_PASSWORD, {
          token: token,
          username: this.username,
        })
        .then((result) => {
          console.log('result', result);
        })
        .catch(() => {
          this.mode = 'failed-password';
        });
    },
  },
  computed: {
    ...mapState({
      errors: (state) => state.auth.errors,
      isAuthenticated: (state) => state.auth.isAuthenticated,
    }),
    appVersion() {
      return process.env.VUE_APP_VERSION || '2.0.0';
    },
    appVersionShort() {
      const parts = this.appVersion.split('.');
      return parts.slice(0, 2).join('.');
    },
  },
};
</script>

<style lang="scss" scoped>
$login-brand-primary: #26a9b5;

.login-brand-block {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.login-brand-logo {
  width: 44px;
  height: 44px;
  object-fit: cover;
  object-position: left center;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 8px 24px rgba(38, 169, 181, 0.35);
}

.login-brand-text {
  text-align: left;
  line-height: 1.2;
}

.login-brand-name {
  font-size: 1.25rem;
  font-weight: 900;
  letter-spacing: -0.02em;
  color: #fff;
}

.login-brand-tagline {
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  color: rgba(255, 255, 255, 0.65);
}

.login-version-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.875rem;
  border-radius: 999px;
  background: rgba(38, 169, 181, 0.18);
  color: #7dd8e3;
  font-size: 0.6875rem;
  font-weight: 600;
  margin-bottom: 1.75rem;
}

.login-form-header {
  margin-bottom: 1.5rem;
}

.login-form-title {
  font-size: 1.75rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 0.375rem;
  line-height: 1.2;
}

.login-form-subtitle {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 0;
}

.login-form-container {
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 1.5rem;
  background: #fff;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
  text-align: left;
}

.login-form-card-title {
  font-size: 1rem;
  color: #111827;
}

.submit-button {
  height: 44px;
  color: #fff;
  background-color: $login-brand-primary;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.15s;
  border: none;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(38, 169, 181, 0.25);

  &:hover:not(:disabled) {
    background-color: #1f8f99;
    color: #fff;
  }

  &:active:not(:disabled) {
    transform: scale(0.99);
  }

  &:disabled {
    opacity: 0.75;
    cursor: not-allowed;
  }
}

.login-btn-spinner {
  width: 16px;
  height: 16px;
  margin-right: 0.5rem;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: login-spin 0.7s linear infinite;
}

@keyframes login-spin {
  to {
    transform: rotate(360deg);
  }
}

.login-security-note {
  font-size: 0.6875rem;
  color: #6b7280;
  display: flex;
  align-items: flex-start;
  line-height: 1.4;
}

.login-help-text {
  margin-top: 1.25rem;
  margin-bottom: 0;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.5;
  text-align: center;

  strong {
    color: rgba(255, 255, 255, 0.85);
    font-weight: 600;
  }

  a {
    color: #7dd8e3;
    font-weight: 600;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.login-form-bottom {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
  font-size: 0.6875rem;
}

.login-back-link {
  color: rgba(255, 255, 255, 0.45);
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
  }
}

.login-bottom-dot {
  color: rgba(255, 255, 255, 0.25);
}

.login-version-text {
  color: rgba(255, 255, 255, 0.45);
}
</style>
